/**
 * Layout for clip-v2. Reference pixels for a 1080-wide canvas, multiplied by
 * getVideoScale(width, height).
 *
 * 9:16 safe area: all text stays between 12% from the top and 20% from the
 * bottom (TikTok / Shorts / Reels UI). Captions sit at the bottom of that band,
 * the stage (visuals) above them. 1:1 feeds only need small margins.
 */
import { getVideoScale } from "../../theme";

export const CLIP_V2_LAYOUTS = {
  vertical: {
    safeTopPct: 0.12,
    safeBottomPct: 0.2,
    sidePad: 64,
    headerHeight: 72,
    headerGap: 26,
    captionHeight: 230,
    captionGap: 18,
    logoSize: 56,
    brandFont: 30,
    chipFont: 28,
    kickerFont: 36,
    hookFont: 116,
    tickerFont: 230,
    spotFont: 64,
    strategyFont: 60,
    legsFont: 38,
    chartFont: 27,
    readoutLabelFont: 28,
    readoutValueFont: 92,
    bigNumberFont: 240,
    bigLabelFont: 40,
    lessonFont: 92,
    ctaFont: 100,
    urlFont: 58,
    captionFont: 66,
    endTitleFont: 112,
    endBodyFont: 32,
    endMetaFont: 30,
    tableFont: 38,
    cardTitleFont: 52,
    cardBodyFont: 32,
    pipWidth: 300,
    pipHeight: 400,
    progressHeight: 10,
  },
  square: {
    safeTopPct: 0.045,
    safeBottomPct: 0.045,
    sidePad: 56,
    headerHeight: 56,
    headerGap: 12,
    captionHeight: 132,
    captionGap: 8,
    logoSize: 44,
    brandFont: 24,
    chipFont: 22,
    kickerFont: 28,
    hookFont: 94,
    tickerFont: 150,
    spotFont: 48,
    strategyFont: 46,
    legsFont: 30,
    chartFont: 22,
    readoutLabelFont: 22,
    readoutValueFont: 66,
    bigNumberFont: 170,
    bigLabelFont: 32,
    lessonFont: 66,
    ctaFont: 74,
    urlFont: 46,
    captionFont: 50,
    endTitleFont: 84,
    endBodyFont: 24,
    endMetaFont: 23,
    tableFont: 30,
    cardTitleFont: 40,
    cardBodyFont: 26,
    pipWidth: 220,
    pipHeight: 220,
    progressHeight: 8,
  },
};

/** Portrait → vertical constants; 1:1 and landscape → square constants. */
export function getClipV2Layout(width, height) {
  const key = height > width ? "vertical" : "square";
  const L = CLIP_V2_LAYOUTS[key];
  const scale = getVideoScale(width, height);
  const s = (value) => Math.round(value * scale);
  const safeTop = Math.round(height * L.safeTopPct);
  const safeBottom = Math.round(height * L.safeBottomPct);
  const sidePad = s(L.sidePad);
  const bandBottom = height - safeBottom;
  const captionTop = bandBottom - s(L.captionHeight);
  const headerTop = safeTop;
  const stageTop = headerTop + s(L.headerHeight) + s(L.headerGap);
  const stageBottom = captionTop - s(L.captionGap);
  return {
    key,
    s,
    L,
    width,
    height,
    sidePad,
    safeTop,
    safeBottom,
    contentWidth: width - sidePad * 2,
    header: { top: headerTop, height: s(L.headerHeight) },
    stage: { top: stageTop, height: stageBottom - stageTop },
    /** Full text band (hook / end card use it all). */
    band: { top: safeTop, height: bandBottom - safeTop },
    captions: { top: captionTop, height: s(L.captionHeight) },
  };
}
